import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { IconShieldCheck, IconCheck, IconPhoto, IconPalette, IconLayout } from '@tabler/icons-react';

const steps = [
  { icon: IconCheck, label: 'Account created' },
  { icon: IconCheck, label: 'Website found' },
  { icon: IconPhoto, label: 'Extracting logo...' },
  { icon: IconPalette, label: 'Extracting brand colours' },
  { icon: IconLayout, label: 'Building your dashboard' },
];

// Clearbit logo API — free, no auth needed
function getLogoUrl(website) {
  const domain = website.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  return `https://logo.clearbit.com/${domain}`;
}

// Extract dominant colour from an image using canvas
async function extractColourFromImage(imageUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let r = 0, g = 0, b = 0, count = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 128) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
      }
      if (count === 0) { resolve('#E8610A'); return; }
      r = Math.round(r / count);
      g = Math.round(g / count);
      b = Math.round(b / count);
      resolve(`#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`);
    };
    img.onerror = () => resolve('#E8610A');
    img.src = imageUrl;
  });
}

export default function BrandScan() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([0, 1]);
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, orgName, website, slug } = location.state || {};

  useEffect(() => {
    if (!orgId || !website) {
      navigate('/signup');
      return;
    }

    const scan = async () => {
      // Step 2 — extracting logo
      setCurrentStep(2);
      await new Promise(r => setTimeout(r, 800));

      const logoUrl = getLogoUrl(website);

      // Step 3 — extracting colours
      setCurrentStep(3);
      setCompletedSteps(prev => [...prev, 2]);
      await new Promise(r => setTimeout(r, 800));

      const primaryColour = await extractColourFromImage(logoUrl);

      // Step 4 — building dashboard
      setCurrentStep(4);
      setCompletedSteps(prev => [...prev, 3]);
      await new Promise(r => setTimeout(r, 600));

      // Update org in Firestore with brand data
      try {
        await updateDoc(doc(db, 'organisations', orgId), {
          brandColour: primaryColour,
          logo: logoUrl,
        });
      } catch (err) {
        console.log('Brand update error:', err);
      }

      setCompletedSteps(prev => [...prev, 4]);
      await new Promise(r => setTimeout(r, 400));

      // Navigate to brand preview
      navigate('/signup/preview', {
        state: { orgId, orgName, website, slug, logoUrl, primaryColour }
      });
    };

    scan();
  }, [orgId, website, navigate, orgName, slug]);

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      fontFamily: 'inherit',
      overflow: 'hidden',
    }}>

      {/* Left panel */}
      <div style={{
        width: '40%',
        background: 'var(--color-brand)',
        padding: '48px 52px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IconShieldCheck size={22} color="white" stroke={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 400, color: '#fff' }}>Veritas</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Fleet &amp; equipment compliance
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}>
            <IconPalette size={26} color="white" stroke={1.5} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 400, color: '#fff', lineHeight: 1.35, marginBottom: 14 }}>
            Setting up your account.
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
            We're pulling your logo and brand colours from your website to personalise your Veritas account.
          </div>
        </div>

        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          An <span style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>AGRIWARE</span> product &copy; 2026
        </div>
      </div>

      {/* Right panel */}
      <div style={{
        flex: 1,
        background: 'var(--color-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 80px',
      }}>

        {/* Spinning icon */}
        <div style={{ position: 'relative', width: 80, height: 80, marginBottom: 28 }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'var(--color-brand-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IconShieldCheck size={36} color="var(--color-brand)" stroke={1.5} />
          </div>
          {/* Spinning ring */}
          <div style={{
            position: 'absolute',
            top: -4,
            left: -4,
            width: 88,
            height: 88,
            borderRadius: '50%',
            border: '2px solid var(--color-brand)',
            borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
        </div>

        <h2 style={{ fontSize: 22, fontWeight: 400, marginBottom: 8 }}>
          Scanning {website}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 36 }}>
          This takes just a few seconds
        </p>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 320 }}>
          {steps.map((step, i) => {
            const isDone = completedSteps.includes(i);
            const isActive = currentStep === i && !isDone;
            const StepIcon = step.icon;

            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: isDone
                    ? 'var(--color-success-light)'
                    : isActive
                      ? 'var(--color-brand-light)'
                      : 'var(--color-bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background 0.3s',
                }}>
                  <StepIcon
                    size={14}
                    stroke={1.5}
                    color={isDone ? 'var(--color-success)' : isActive ? 'var(--color-brand)' : 'var(--color-text-tertiary)'}
                  />
                </div>
                <span style={{
                  fontSize: 13,
                  color: isDone
                    ? 'var(--color-success)'
                    : isActive
                      ? 'var(--color-brand)'
                      : 'var(--color-text-tertiary)',
                  fontWeight: isDone || isActive ? 400 : 400,
                  transition: 'color 0.3s',
                }}>
                  {isDone ? step.label.replace('...', '') : step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}